import { useState } from 'react';

/**
 * Dismissal capture — the panel from the design project's _dismiss.js.
 *
 * A dismissal is training data, so the reason is worth asking for even though it
 * is optional. Forcing one produces a reason picked at random, which is worse
 * than none: it teaches the model something nobody meant.
 *
 * THE WARNING IS THE POINT. Several reasons mean the finding was RIGHT —
 * "Already actioned outside Stellair", "Wrong timing", "Budget is fixed by the
 * client". Choosing one shows, at that moment, that it will be recorded as a
 * deferral / resolved-elsewhere / constraint rather than a rejection, and that
 * the check keeps running. Without that line a person reasonably assumes
 * dismissing means "stop telling me", and the model learns to drop a correct
 * finding.
 *
 * The outcome itself is derived SERVER-SIDE from the reason
 * (utils/performanceDismissals.js). This component only shows what will happen.
 */

const SCOPES = [
  ['finding', 'Just this finding'],
  ['campaign_check', 'This campaign, this check'],
  ['account_check', 'This ad account, this check'],
];

/**
 * The scopes THIS card can actually be dismissed under.
 *
 * "This campaign, this check" NAMES a campaign, and the route refuses it
 * without one — correctly, since without a campaign the dismissal would
 * silently widen to the whole account. A Moments trigger has no campaign by
 * construction: it is evaluated against the market and fans out across every
 * campaign it matches, which is why it carries an explicit `campaignId: null`.
 *
 * So the option was offered on every moment and could never succeed. Offering a
 * choice that cannot be honoured is the same failure as a button that does
 * nothing — the fix is not to show it.
 */
export function scopesFor(card) {
  const campaign = card && ('campaignId' in card ? card.campaignId : card.camp);
  return campaign ? SCOPES : SCOPES.filter(([v]) => v !== 'campaign_check');
}

const DURATIONS = [
  ['until_change', 'Until something changes'],
  ['7d', '7 days'],
  ['30d', '30 days'],
  ['flight', 'This flight'],
  ['permanent', 'Permanently'],
];

export default function DismissPanel({ card, reasons = [], notes = {}, onConfirm, onCancel }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');
  const [scope, setScope] = useState('finding');
  const [duration, setDuration] = useState('until_change');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  const warning = reason ? notes[reason] : null;
  const scopes = scopesFor(card);

  if (done) {
    return (
      <div className="dsm">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 12.5, color: 'var(--grn)' }}>
          <span aria-hidden="true" className="material-icons" style={{ fontSize: 17 }}>check_circle</span>
          <span>
            {done.reason ? <>Dismissed — <b>{done.reason}</b>. </> : 'Dismissed. '}
            This finding will not re-raise under the scope you chose.
            {!done.reason && ' No reason was given, so nothing is learned from it beyond the scope.'}
            {done.note ? <><br /><span style={{ color: 'var(--mut)' }}>&ldquo;{done.note}&rdquo;</span></> : null}
          </span>
        </div>
      </div>
    );
  }

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onConfirm({ reason, note: note.trim() || null, scope, duration });
      setDone({ reason, note: note.trim() });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not record the dismissal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dsm">
      <div className="dsm-h">
        Why are you dismissing this? <span className="dsm-req">optional</span>
      </div>

      <div className="dsm-r">
        {reasons.map((r) => (
          <button
            key={r}
            type="button"
            className={`dsm-c${reason === r ? ' on' : ''}`}
            // A reason is optional, so a chosen chip can be turned back off.
            onClick={() => setReason((cur) => (cur === r ? null : r))}
          >
            {r}
          </button>
        ))}
      </div>

      <textarea
        className="dsm-ta"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add context — what should we know next time? (optional, but this is what a teammate reads in six weeks)"
      />

      <div className={`dsm-warn${warning ? ' show' : ''}`}>{warning || ''}</div>

      <div className="dsm-opts">
        <div className="dsm-o">
          <span className="dsm-ol">Applies to</span>
          <select aria-label="Dismissal scope" className="dsm-sel" value={scope} onChange={(e) => setScope(e.target.value)}>
            {scopes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="dsm-o">
          <span className="dsm-ol">For how long</span>
          <select aria-label="Dismissal duration" className="dsm-sel" value={duration} onChange={(e) => setDuration(e.target.value)}>
            {DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <p className="dsm-scope-help">{scope === 'finding'
        ? 'Hide this specific recommendation on this campaign. Other findings stay visible.'
        : scope === 'campaign_check'
          ? 'Hide recommendations from this analysis with the same action type throughout this campaign.'
          : 'Hide recommendations from this analysis with the same action type across all campaigns in this account.'}</p>

      {error ? <div className="dsm-warn show" style={{ borderLeftColor: 'var(--red)' }}>{error}</div> : null}

      <div className="dsm-foot">
        <button type="button" className="btn btn-pri btn-sm" onClick={submit} disabled={saving}>
          {saving ? 'Dismissing…' : 'Dismiss'}
        </button>
        <button type="button" className="btn btn-gho btn-sm" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <div className="dsm-note">
          Shared with everyone in this account; your name records who dismissed it.
          Your reason provides context for future analyses. This is not a personal preference.
        </div>
      </div>
    </div>
  );
}

export { SCOPES, DURATIONS };
